import { Component, inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AtlasConfigService, AtlasDataService } from '../../../core/src';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { IAMService } from '@vendasta/iamv2';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PAM_ESCAPE_FN, PamEscapeFn } from '../module-common';

interface Entitlement {
  id: string;
  name: string;
  description: string;
}

interface EntitlementState {
  error: boolean;
  loading: boolean;
  entitlement?: Entitlement;
}

export enum PamReason {
  CustomerSupport = 'Customer Support',
  Debugging = 'Debugging',
  DeveloperOperations = 'Developer Operations',
  ProductActivation = 'Product Activation',
  ProductDemo = 'Product Demo',
  Sales = 'Sales',
  Testing = 'Testing',
  Other = 'Other',
}

interface PamDialogData {
  partnerId: string;
  accountGroupId?: string;
  defaultReason?: string;
  entitlementId?: string;
}

const COMMON_REASONS = [
  PamReason.CustomerSupport,
  PamReason.Debugging,
  PamReason.DeveloperOperations,
  PamReason.ProductActivation,
  PamReason.ProductDemo,
  PamReason.Sales,
  PamReason.Testing,
  PamReason.Other,
];

@Component({
  selector: 'atlas-pam-dialog',
  templateUrl: './pam-dialog.component.html',
  styleUrl: './pam-dialog.component.scss',
  standalone: false,
})
export class PamDialogComponent implements OnInit {
  protected dialogRef = inject(MatDialogRef<PamDialogComponent>);
  private configService = inject(AtlasConfigService);
  private iamService = inject(IAMService);
  private snackbarService = inject(SnackbarService);
  private dataService = inject(AtlasDataService);
  protected escapeFn = inject<PamEscapeFn>(PAM_ESCAPE_FN, { optional: true });

  public data = inject<PamDialogData>(MAT_DIALOG_DATA);
  public readonly COMMON_REASONS = COMMON_REASONS;
  public reasonType = new FormControl<string>(this.data.defaultReason || PamReason.CustomerSupport, {
    nonNullable: true,
  });
  public reason = new FormControl<string>('', { nonNullable: true });
  public entitlementState$!: Observable<EntitlementState>;

  constructor() {
    this.reasonType.valueChanges.pipe(takeUntilDestroyed()).subscribe((v) => {
      if (v !== PamReason.Other) {
        this.reason.setValue('');
      }
    });
  }

  ngOnInit(): void {
    this.entitlementState$ = this.configService.config$.pipe(
      switchMap((config) => this.iamService.getEntitlement(this.data.entitlementId || config.entitlementId)),
      map(
        (resp): EntitlementState => ({
          entitlement: {
            id: resp.entitlement.id,
            name: resp.entitlement.name,
            description: resp.entitlement.description,
          },
          loading: false,
          error: false,
        }),
      ),
      catchError(() =>
        of({
          loading: false,
          error: true,
        }),
      ),
      startWith({
        loading: true,
        error: false,
      }),
    );
  }

  async createGrant() {
    const rt = this.reasonType.value;
    if (!rt || !rt.trim()) {
      this.reasonType.markAsTouched();
      return;
    }

    const isOther = rt === 'Other';
    if (isOther) {
      const r = this.reason.value;
      if (!r || !r.trim()) {
        this.reason.markAsTouched();
        this.snackbarService.openErrorSnack('Please provide a reason for access');
        return;
      }
    }

    try {
      const reasonValue = isOther ? this.reason.value : rt;
      await firstValueFrom(
        combineLatest([this.dataService.userId$, this.configService.config$]).pipe(
          switchMap(([userId, cfg]) => {
            this.data.partnerId = this.data.partnerId || cfg.partnerId;
            this.data.accountGroupId = this.data.accountGroupId || cfg.accountId;
            return this.iamService.createPamGrant(
              userId,
              this.data.entitlementId || cfg.entitlementId,
              reasonValue,
              this.data.partnerId,
              this.data.accountGroupId,
            );
          }),
        ),
      );
      this.dialogRef.close(true);
    } catch {
      this.snackbarService.openErrorSnack('Unable to create privileged access grant');
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  async goBack(): Promise<void> {
    if (this.escapeFn) {
      await this.escapeFn();
    }
  }
}
